import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = '';

function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [passphrase, setPassphrase] = useState('');
  const [loginError, setLoginError] = useState('');
  const [logging, setLogging] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [fdStats, setFdStats] = useState(null);
  const [fdAccounts, setFdAccounts] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [trafficDays, setTrafficDays] = useState(30);
  const [includeBots, setIncludeBots] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [carts, setCarts] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [fdReviews, setFdReviews] = useState([]);
  const [fdReviewsFilter, setFdReviewsFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const headers = { 'x-admin-token': token, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async (endpoint) => {
    const res = await fetch(`${API}/api/admin/${endpoint}`, { headers: { 'x-admin-token': token } });
    if (res.status === 401) {
      setToken('');
      sessionStorage.removeItem('admin_token');
      return null;
    }
    if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
    return res.json();
  }, [token]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [s, fs] = await Promise.all([fetchData('stats'), fetchData('fd-stats')]);
      if (s) setStats(s);
      if (fs) setFdStats(fs);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [fetchData]);

  useEffect(() => {
    if (token) loadDashboard();
  }, [token, loadDashboard]);

  useEffect(() => {
    document.title = 'Admin Dashboard — ObitAuthor';
    return () => { document.title = 'ObitAuthor — Professional Obituary Writing'; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase })
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        sessionStorage.setItem('admin_token', data.token);
        setPassphrase('');
      } else {
        setLoginError(data.error);
      }
    } catch {
      setLoginError('Connection failed. Please try again.');
    }
    setLogging(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/admin/logout`, { method: 'POST', headers });
    } catch { /* ignore */ }
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  const loadTraffic = useCallback(async () => {
    setLoading(true);
    try {
      const t = await fetchData(`traffic?days=${trafficDays}${includeBots ? '&includeBots=1' : ''}`);
      if (t) setTraffic(t);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [fetchData, trafficDays, includeBots]);

  const loadTab = async (tab) => {
    setActiveTab(tab);
    setLoading(true);
    try {
      if (tab === 'overview') {
        const [s, fs] = await Promise.all([fetchData('stats'), fetchData('fd-stats')]);
        if (s) setStats(s);
        if (fs) setFdStats(fs);
      } else if (tab === 'purchases') {
        const p = await fetchData('purchases');
        if (p) setPurchases(p);
      } else if (tab === 'carts') {
        const c = await fetchData('abandoned-carts');
        if (c) setCarts(c);
      } else if (tab === 'generations') {
        const g = await fetchData('generations');
        if (g) setGenerations(g);
      } else if (tab === 'fd-accounts') {
        const [a, fs] = await Promise.all([fetchData('fd-accounts'), fetchData('fd-stats')]);
        if (a) setFdAccounts(a);
        if (fs) setFdStats(fs);
      } else if (tab === 'traffic') {
        const t = await fetchData(`traffic?days=${trafficDays}${includeBots ? '&includeBots=1' : ''}`);
        if (t) setTraffic(t);
      } else if (tab === 'fd-reviews') {
        const r = await fetchData(`fd-reviews?status=${fdReviewsFilter}`);
        if (r) setFdReviews(r);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token && activeTab === 'traffic') loadTraffic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficDays, includeBots]);

  const loadFdReviews = useCallback(async (status) => {
    setLoading(true);
    try {
      const r = await fetchData(`fd-reviews?status=${status}`);
      if (r) setFdReviews(r);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [fetchData]);

  useEffect(() => {
    if (token && activeTab === 'fd-reviews') loadFdReviews(fdReviewsFilter);
  }, [token, activeTab, fdReviewsFilter, loadFdReviews]);

  const moderateReview = async (id, action) => {
    let note = null;
    if (action === 'reject') {
      note = window.prompt('Optional note back to the director (visible to them):', '') || null;
    }
    try {
      const res = await fetch(`${API}/api/admin/fd-reviews/${id}/moderate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action, note }),
      });
      if (res.ok) {
        await loadFdReviews(fdReviewsFilter);
      } else {
        alert('Could not update review');
      }
    } catch (e) { console.error(e); alert('Could not update review'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDay = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
  const fmtNum = (n) => Number(n || 0).toLocaleString();

  const deleteCart = async (id) => {
    if (!window.confirm('Delete this abandoned cart?')) return;
    try {
      const res = await fetch(`${API}/api/admin/abandoned-carts/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } });
      if (res.ok) setCarts(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
  };

  const adjustCredits = async (id, delta) => {
    if (!delta || Number.isNaN(delta)) return;
    try {
      const res = await fetch(`${API}/api/admin/fd-accounts/${id}/adjust-credits`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ delta }),
      });
      if (res.ok) {
        const data = await res.json();
        setFdAccounts(prev => prev.map(a => a.id === id ? { ...a, credits: data.credits } : a));
      } else {
        alert('Could not adjust credits');
      }
    } catch (e) { console.error(e); alert('Could not adjust credits'); }
  };

  const promptAdjust = (account) => {
    const raw = window.prompt(
      `Adjust credits for ${account.email} (current: ${account.credits}).\nEnter +N to add, -N to subtract:`,
      '+10'
    );
    if (!raw) return;
    const delta = parseInt(String(raw).trim(), 10);
    if (Number.isNaN(delta) || delta === 0) return;
    adjustCredits(account.id, delta);
  };

  const deleteFdAccount = async (account) => {
    if (!window.confirm(`Delete account ${account.email}?\nThis also deletes their saved obituaries and service locations.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/fd-accounts/${account.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token },
      });
      if (res.ok) {
        setFdAccounts(prev => prev.filter(a => a.id !== account.id));
      } else {
        alert('Could not delete account');
      }
    } catch (e) { console.error(e); alert('Could not delete account'); }
  };

  if (!token) {
    return (
      <div className="container py-5" style={{ position: 'relative', zIndex: 2 }}>
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="glass-card text-center">
              <h2 className="commercial-heading mb-4" style={{ fontSize: '1.6rem' }}>Admin Dashboard</h2>
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter passphrase"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoFocus
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.3)', color: '#e2e8f0', textAlign: 'center', padding: '12px' }}
                  />
                </div>
                {loginError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{loginError}</div>}
                <button type="submit" disabled={logging || !passphrase} className="btn w-100" style={{ background: 'linear-gradient(135deg, #d4af37 0%, #c4a030 100%)', color: '#1a1a2e', fontWeight: 600 }}>
                  {logging ? 'Verifying...' : 'Enter'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabStyle = (t) => ({
    background: activeTab === t ? 'rgba(212,175,55,0.15)' : 'transparent',
    color: activeTab === t ? '#d4af37' : '#a0aec0',
    border: activeTab === t ? '1px solid rgba(212,175,55,0.3)' : '1px solid transparent',
    borderRadius: '8px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: activeTab === t ? 600 : 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s'
  });

  const cardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(212,175,55,0.15)',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center'
  };

  const bigNum = { fontSize: '2rem', fontWeight: 700, color: '#d4af37', marginBottom: '4px' };
  const label = { fontSize: '0.8rem', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const tableWrap = {
    overflowX: 'auto',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(212,175,55,0.1)',
    borderRadius: '12px'
  };

  const thStyle = {
    background: 'rgba(212,175,55,0.08)',
    color: '#d4af37',
    borderBottom: '1px solid rgba(212,175,55,0.15)',
    padding: '10px 14px',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    fontSize: '0.88rem',
    color: '#e2e8f0',
    verticalAlign: 'middle'
  };

  const sectionHeading = {
    color: '#d4af37',
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: '1.5rem 0 0.75rem',
    letterSpacing: '0.02em',
  };

  const satisfactionEmoji = { happy: '😊', indifferent: '😐', sad: '😞' };

  const pillBtn = (color, bg, border) => ({
    background: bg,
    color,
    border: `1px solid ${border}`,
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    marginRight: '4px',
  });

  return (
    <div style={{ background: 'rgba(26,26,46,0.95)', minHeight: '80vh', padding: '24px 0', position: 'relative', zIndex: 2 }}>
    <div className="container-fluid py-0" style={{ maxWidth: '1280px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="commercial-heading mb-0" style={{ fontSize: '1.4rem' }}>ObitAuthor Admin</h2>
        <button onClick={handleLogout} className="btn btn-sm" style={{ color: '#a0aec0', border: '1px solid rgba(255,255,255,0.1)' }}>Logout</button>
      </div>

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[
          ['overview', '📊 Overview'],
          ['traffic', '📈 Traffic'],
          ['purchases', '💳 Purchases'],
          ['generations', '📝 Obituaries'],
          ['carts', '🛒 Abandoned Carts'],
          ['fd-accounts', '🏛️ Funeral Pros'],
          ['fd-reviews', '⭐ FD Reviews'],
        ].map(([id, label]) => (
          <div key={id} style={tabStyle(id)} onClick={() => loadTab(id)}>
            {label}
          </div>
        ))}
      </div>

      {loading && <div className="text-center py-4" style={{ color: '#a0aec0' }}>Loading...</div>}

      {!loading && activeTab === 'overview' && stats && (
        <>
          <div style={sectionHeading}>Consumer site</div>
          <div className="row g-3 mb-3">
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.purchases.total}</div>
                <div style={label}>Total Purchases</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtMoney(stats.purchases.revenue)}</div>
                <div style={label}>Total Revenue</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.purchases.last30Days}</div>
                <div style={label}>Last 30 Days</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtNum(stats.visitors)}</div>
                <div style={label}>Visitor Counter</div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.codes.total}</div>
                <div style={label}>Total Codes</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.codes.redeemed}</div>
                <div style={label}>Codes Used</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.obituaries?.total || 0}</div>
                <div style={label}>Obituaries Generated</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.abandonedCarts.total}</div>
                <div style={label}>Abandoned Carts</div>
              </div>
            </div>
          </div>

          {fdStats && (
            <>
              <div style={sectionHeading}>Funeral Professional (B2B)</div>
              <div className="row g-3 mb-3">
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.accounts)}</div>
                    <div style={label}>FD Accounts</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtMoney(fdStats.totals.revenue)}</div>
                    <div style={label}>Total B2B Revenue</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.obituaries)}</div>
                    <div style={label}>B2B Obituaries</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.credits_outstanding)}</div>
                    <div style={label}>Credits Outstanding</div>
                  </div>
                </div>
              </div>
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.new_accounts_30d)}</div>
                    <div style={label}>New Accts (30d)</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtMoney(fdStats.totals.revenue_30d)}</div>
                    <div style={label}>Revenue (30d)</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.obituaries_30d)}</div>
                    <div style={label}>Obituaries (30d)</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div style={cardStyle}>
                    <div style={bigNum}>{fmtNum(fdStats.totals.credits_sold)}</div>
                    <div style={label}>Credits Sold (all-time)</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {Object.keys(stats.satisfaction || {}).length > 0 && (
            <div style={{ ...cardStyle, textAlign: 'left', marginBottom: '1rem' }}>
              <div style={{ ...label, marginBottom: '12px' }}>Satisfaction Ratings</div>
              <div className="d-flex gap-4">
                {Object.entries(stats.satisfaction).map(([key, count]) => (
                  <div key={key}>
                    <span style={{ fontSize: '1.5rem' }}>{satisfactionEmoji[key] || key}</span>
                    <span style={{ color: '#e2e8f0', marginLeft: '8px', fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!loading && activeTab === 'traffic' && (
        <TrafficPanel
          traffic={traffic}
          days={trafficDays}
          setDays={setTrafficDays}
          includeBots={includeBots}
          setIncludeBots={setIncludeBots}
          onRefresh={loadTraffic}
          cardStyle={cardStyle}
          bigNum={bigNum}
          label={label}
          tableWrap={tableWrap}
          thStyle={thStyle}
          tdStyle={tdStyle}
          fmtNum={fmtNum}
          fmtDay={fmtDay}
        />
      )}

      {!loading && activeTab === 'purchases' && (
        <div style={tableWrap}>
          <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Codes</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && (
                <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No purchases yet</td></tr>
              )}
              {purchases.map(p => (
                <tr key={p.id}>
                  <td style={tdStyle}>#{p.id}</td>
                  <td style={tdStyle}>{p.email}</td>
                  <td style={tdStyle}>{fmtMoney(p.amount)}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(p.purchase_date)}</td>
                  <td style={tdStyle}>
                    {p.codes.map((c, i) => (
                      <span key={i} style={{
                        display: 'inline-block',
                        background: c.redeemed ? 'rgba(72,187,120,0.15)' : 'rgba(160,174,192,0.1)',
                        color: c.redeemed ? '#68d391' : '#a0aec0',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '0.75rem',
                        marginRight: '4px',
                        marginBottom: '2px',
                        fontFamily: 'monospace'
                      }}>
                        {c.code} {c.redeemed ? '✓' : ''}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'generations' && (
        <div style={tableWrap}>
          <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Deceased Name</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Rating</th>
                <th style={thStyle}>Retries</th>
                <th style={thStyle}>Code</th>
              </tr>
            </thead>
            <tbody>
              {generations.length === 0 && (
                <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No obituaries yet</td></tr>
              )}
              {generations.map(g => (
                <tr key={g.id}>
                  <td style={tdStyle}>#{g.id}</td>
                  <td style={tdStyle}>{g.deceased_name || '—'}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(g.created_at)}</td>
                  <td style={tdStyle}>{g.satisfaction_rating ? satisfactionEmoji[g.satisfaction_rating] : '—'}</td>
                  <td style={tdStyle}>{g.retry_count || 0}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>{g.code || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'carts' && (
        <div style={tableWrap}>
          <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Recovery Email</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {carts.length === 0 && (
                <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No abandoned carts</td></tr>
              )}
              {carts.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}>#{c.id}</td>
                  <td style={tdStyle}>{c.email}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: c.email_sent ? 'rgba(72,187,120,0.15)' : 'rgba(245,101,101,0.15)',
                      color: c.email_sent ? '#68d391' : '#fc8181',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '0.8rem'
                    }}>
                      {c.email_sent ? 'Sent' : 'Not sent'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => deleteCart(c.id)} style={{ background: 'rgba(245,101,101,0.15)', color: '#fc8181', border: '1px solid rgba(245,101,101,0.3)', borderRadius: '4px', padding: '2px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'fd-accounts' && (
        <>
          {fdStats && (
            <div className="row g-3 mb-3">
              <div className="col-6 col-md-3">
                <div style={cardStyle}>
                  <div style={bigNum}>{fmtNum(fdStats.totals.accounts)}</div>
                  <div style={label}>Total Accounts</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div style={cardStyle}>
                  <div style={bigNum}>{fmtMoney(fdStats.totals.revenue)}</div>
                  <div style={label}>Total Revenue</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div style={cardStyle}>
                  <div style={bigNum}>{fmtNum(fdStats.totals.obituaries)}</div>
                  <div style={label}>Obituaries</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div style={cardStyle}>
                  <div style={bigNum}>{fmtNum(fdStats.totals.credits_outstanding)}</div>
                  <div style={label}>Credits Held</div>
                </div>
              </div>
            </div>
          )}

          <div style={tableWrap}>
            <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
              <thead>
                <tr>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Firm</th>
                  <th style={thStyle}>Representative</th>
                  <th style={thStyle}>Credits</th>
                  <th style={thStyle}>Obits</th>
                  <th style={thStyle}>Spend</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Last Order</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fdAccounts.length === 0 && (
                  <tr><td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No funeral professional accounts yet</td></tr>
                )}
                {fdAccounts.map(a => (
                  <tr key={a.id}>
                    <td style={tdStyle}>#{a.id}</td>
                    <td style={tdStyle}>{a.email}</td>
                    <td style={tdStyle}>{a.company_name || '—'}</td>
                    <td style={tdStyle}>{a.representative_name || '—'}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: a.credits <= 10 ? '#fc8181' : '#e2e8f0' }}>
                      {fmtNum(a.credits)}
                    </td>
                    <td style={tdStyle}>{fmtNum(a.obit_count)}</td>
                    <td style={tdStyle}>{fmtMoney(a.total_spend)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{a.last_order_at ? fmtDate(a.last_order_at) : '—'}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <button onClick={() => adjustCredits(a.id, 10)} style={pillBtn('#68d391', 'rgba(72,187,120,0.12)', 'rgba(72,187,120,0.35)')}>+10</button>
                      <button onClick={() => adjustCredits(a.id, 50)} style={pillBtn('#68d391', 'rgba(72,187,120,0.12)', 'rgba(72,187,120,0.35)')}>+50</button>
                      <button onClick={() => promptAdjust(a)} style={pillBtn('#d4af37', 'rgba(212,175,55,0.12)', 'rgba(212,175,55,0.35)')}>±N</button>
                      <button onClick={() => deleteFdAccount(a)} style={pillBtn('#fc8181', 'rgba(245,101,101,0.12)', 'rgba(245,101,101,0.35)')}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && activeTab === 'fd-reviews' && (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
            {['pending', 'approved', 'rejected', 'all'].map((s) => (
              <button
                key={s}
                onClick={() => setFdReviewsFilter(s)}
                style={{
                  background: fdReviewsFilter === s ? 'rgba(212,175,55,0.2)' : 'transparent',
                  color: fdReviewsFilter === s ? '#d4af37' : '#a0aec0',
                  border: `1px solid ${fdReviewsFilter === s ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '0.85rem',
                  textTransform: 'capitalize',
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
            <span style={{ color: '#a0aec0', fontSize: '0.85rem', marginLeft: 8 }}>
              {fdReviews.length} result{fdReviews.length === 1 ? '' : 's'}
            </span>
          </div>
          <div style={tableWrap}>
            <table className="table mb-0" style={{ color: '#e2e8f0' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Submitted</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Rating</th>
                  <th style={thStyle}>Director / firm</th>
                  <th style={thStyle}>Review</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fdReviews.length === 0 && (
                  <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No reviews match that filter.</td></tr>
                )}
                {fdReviews.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{fmtDate(r.created_at)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem',
                        background:
                          r.status === 'approved' ? 'rgba(74,222,128,0.15)' :
                          r.status === 'rejected' ? 'rgba(248,113,113,0.15)' :
                          'rgba(250,204,21,0.15)',
                        color:
                          r.status === 'approved' ? '#86efac' :
                          r.status === 'rejected' ? '#fca5a5' : '#fde68a',
                      }}>{r.status}</span>
                    </td>
                    <td style={tdStyle}>{r.rating} ★</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{r.display_name || '(no name)'} <span style={{ color: '#a0aec0', fontWeight: 400 }}>· {r.role || 'Director'}</span></div>
                      <div style={{ color: '#a0aec0', fontSize: '0.78rem' }}>
                        {r.firm_display || r.company_name || '—'}
                        {r.state_code ? `, ${r.state_code}` : ''}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{r.account_email}</div>
                      {!r.allow_public && (
                        <div style={{ color: '#fca5a5', fontSize: '0.72rem' }}>Not marked public by director</div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 420, whiteSpace: 'normal' }}>{r.body}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.status !== 'approved' && (
                          <button style={pillBtn('#86efac', 'rgba(74,222,128,0.12)', 'rgba(74,222,128,0.3)')} onClick={() => moderateReview(r.id, 'approve')}>Approve</button>
                        )}
                        {r.status !== 'rejected' && (
                          <button style={pillBtn('#fca5a5', 'rgba(248,113,113,0.12)', 'rgba(248,113,113,0.3)')} onClick={() => moderateReview(r.id, 'reject')}>Reject</button>
                        )}
                        {r.status !== 'pending' && (
                          <button style={pillBtn('#fde68a', 'rgba(250,204,21,0.12)', 'rgba(250,204,21,0.3)')} onClick={() => moderateReview(r.id, 'pending')}>Unmoderate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
    </div>
  );
}

/* ------------------------------------------------------------
   Traffic panel with a small inline SVG line chart for daily
   views + tables for top pages, referrers, and device mix.
   ------------------------------------------------------------ */
function TrafficPanel({
  traffic, days, setDays, includeBots, setIncludeBots, onRefresh,
  cardStyle, bigNum, label, tableWrap, thStyle, tdStyle, fmtNum, fmtDay,
}) {
  const chart = useMemo(() => {
    if (!traffic || !traffic.daily || traffic.daily.length === 0) return null;
    const W = 720;
    const H = 160;
    const padL = 30;
    const padR = 10;
    const padT = 10;
    const padB = 22;
    const points = traffic.daily.map(d => ({
      day: d.day,
      views: Number(d.views || 0),
      sessions: Number(d.sessions || 0),
      uniques: Number(d.unique_visitors || 0),
    }));
    const maxY = Math.max(1, ...points.map(p => Math.max(p.views, p.sessions, p.uniques)));
    const stepX = points.length > 1 ? (W - padL - padR) / (points.length - 1) : 0;
    const scaleY = (v) => H - padB - ((H - padT - padB) * (v / maxY));
    const mkPath = (key) => points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${padL + i * stepX} ${scaleY(p[key])}`).join(' ');
    const tickCount = Math.min(points.length, 6);
    const tickIdx = Array.from({ length: tickCount }, (_, i) => Math.round(i * (points.length - 1) / Math.max(1, tickCount - 1)));
    return { W, H, padL, padR, padT, padB, points, maxY, stepX, scaleY, mkPath, tickIdx };
  }, [traffic]);

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {[7, 14, 30, 60, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              background: days === d ? 'rgba(212,175,55,0.2)' : 'transparent',
              color: days === d ? '#d4af37' : '#a0aec0',
              border: `1px solid ${days === d ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {d}d
          </button>
        ))}
        <label style={{ color: '#a0aec0', fontSize: '0.85rem', marginLeft: '0.5rem', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
          <input type="checkbox" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} /> Include bots
        </label>
        <button
          onClick={onRefresh}
          style={{ background: 'transparent', color: '#a0aec0', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer', marginLeft: 'auto' }}
        >
          ↻ Refresh
        </button>
      </div>

      {!traffic && <div style={{ color: '#a0aec0' }}>No traffic data yet.</div>}

      {traffic && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtNum(traffic.summary.views)}</div>
                <div style={label}>Page views ({days}d)</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtNum(traffic.summary.sessions)}</div>
                <div style={label}>Sessions ({days}d)</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtNum(traffic.summary.unique_visitors)}</div>
                <div style={label}>Unique visitors</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtNum(traffic.summary.views_24h)}</div>
                <div style={label}>Views last 24h</div>
              </div>
            </div>
          </div>

          {chart && (
            <div style={{ ...cardStyle, textAlign: 'left', marginBottom: '1rem' }}>
              <div style={{ ...label, marginBottom: '10px' }}>Daily traffic</div>
              <svg width="100%" viewBox={`0 0 ${chart.W} ${chart.H}`} style={{ display: 'block' }}>
                {/* gridlines */}
                {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                  const y = chart.padT + (chart.H - chart.padT - chart.padB) * f;
                  return (
                    <g key={i}>
                      <line x1={chart.padL} y1={y} x2={chart.W - chart.padR} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
                      <text x={chart.padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#a0aec0">
                        {fmtNum(Math.round(chart.maxY * (1 - f)))}
                      </text>
                    </g>
                  );
                })}
                {/* lines */}
                <path d={chart.mkPath('views')} fill="none" stroke="#d4af37" strokeWidth="2" />
                <path d={chart.mkPath('sessions')} fill="none" stroke="#68d391" strokeWidth="1.5" />
                <path d={chart.mkPath('uniques')} fill="none" stroke="#63b3ed" strokeWidth="1.5" strokeDasharray="4 3" />
                {/* x ticks */}
                {chart.tickIdx.map((i) => (
                  <text
                    key={i}
                    x={chart.padL + i * chart.stepX}
                    y={chart.H - 6}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#a0aec0"
                  >
                    {fmtDay(chart.points[i].day)}
                  </text>
                ))}
              </svg>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#a0aec0', marginTop: '8px' }}>
                <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#d4af37', marginRight: 6, verticalAlign: 'middle' }} /> views</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#68d391', marginRight: 6, verticalAlign: 'middle' }} /> sessions</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#63b3ed', marginRight: 6, verticalAlign: 'middle', borderTop: '2px dashed #63b3ed' }} /> unique visitors</span>
              </div>
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-7">
              <div style={{ ...label, marginBottom: '6px' }}>Top pages</div>
              <div style={tableWrap}>
                <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Path</th>
                      <th style={thStyle}>Views</th>
                      <th style={thStyle}>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traffic.topPages.map((p, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.82rem' }}>{p.path}</td>
                        <td style={tdStyle}>{fmtNum(p.views)}</td>
                        <td style={tdStyle}>{fmtNum(p.sessions)}</td>
                      </tr>
                    ))}
                    {traffic.topPages.length === 0 && (
                      <tr><td colSpan="3" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="col-md-5">
              <div style={{ ...label, marginBottom: '6px' }}>Top referrers</div>
              <div style={tableWrap}>
                <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Source</th>
                      <th style={thStyle}>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traffic.topReferrers.map((r, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{r.host}</td>
                        <td style={tdStyle}>{fmtNum(r.views)}</td>
                      </tr>
                    ))}
                    {traffic.topReferrers.length === 0 && (
                      <tr><td colSpan="2" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ ...label, marginTop: '1rem', marginBottom: '6px' }}>Device mix</div>
              <div style={tableWrap}>
                <table className="table table-dark mb-0" style={{ background: 'transparent' }}>
                  <tbody>
                    {traffic.devices.map((d, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, textTransform: 'capitalize' }}>{d.device}</td>
                        <td style={tdStyle}>{fmtNum(d.views)}</td>
                      </tr>
                    ))}
                    {traffic.devices.length === 0 && (
                      <tr><td colSpan="2" style={{ ...tdStyle, textAlign: 'center', color: '#a0aec0' }}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Admin;
